import { Button, DocumentCopyDropdown, InlineTeaserView, Input, Prompt, Select } from '@components'
import { ContactSelector } from '@components/uas/ContactSelector'
import { BLUEPRINT_TYPE_OPTIONS, NOCFO_PLAN_OPTIONS, getSettingsPageUrl } from '@constants'
import { isBusinessBillingLocked, useTrialLimits } from '@query'
import { useBusinessContext } from '@context'
import { BlueprintEditor } from '@pages/DocumentPage/DocumentForm/BlueprintEditor'
import { ControlledDocumentAttachmentsField } from '@pages/DocumentPage/DocumentForm/DocumentAttachmentsField'
import {
  getSuggestionDefaultAccrualDate,
  getSuggestionDefaultPostingMode,
  IAccountingSuggestion
} from '@query/suggestions'
import { getDocumentCopyValues } from '@utils/copyDocument'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, get, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaCheck, FaExclamationTriangle } from 'react-icons/fa'
import styled from 'styled-components'
import { cleanBlueprint, getOptionLabelByValue, setAPIErrors, useScreen } from '@utils'
import { resolveAcceptSuggestionError } from './resolveAcceptSuggestionError'
import { useAllowUpload } from '@root/hooks/useAllowUpload'
import { useDocumentLimitStatus } from '@hooks'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import ContactSuggestion from './ContactSuggestion'
import {
  BlueprintDebetCreditDiffSync,
  BlueprintPaymentAmountSync,
  ConfirmPostingWarningReason,
  expectedAmountForPaymentAccount,
  getConfirmPostingWarning,
  postingWarningDescriptionKey,
  postingWarningTitleKey,
  useUpdateEntryAmounts
} from '../../DocumentForm/hooks'
import { useEuSaleWarning } from '../../DocumentForm/euB2bVatDetails'
import { EuB2bVatFieldWrap, EuB2bVatWarning } from '../../DocumentForm/EuB2bVatWarning'
import toast from 'react-hot-toast'
import { FormSectionEyebrow } from '../../DocumentForm/FormSectionEyebrow'
import RelationMapper, { DocumentRelationFormValue } from '../../DocumentForm/RelationMapper'
import { RelationsHeader } from '../../DocumentForm/RelationMapper.styled'
import { DocumentHelpLink } from '../../DocumentForm/DocumentHelpLink'
import { mapRelationsForForm } from '../../DocumentForm/relationMapperUtils'

type SuggestionFormValues = IAccountingSuggestion & {
  relations?: DocumentRelationFormValue[]
  attachment_exempt: boolean
}

const getSuggestionFormDefaultValues = (
  suggestion: IAccountingSuggestion,
  attachmentExempt: boolean
): SuggestionFormValues => {
  const blueprint = suggestion?.blueprint || {}
  const defaultPostingMode = getSuggestionDefaultPostingMode(suggestion)
  const defaultAccrualDate = getSuggestionDefaultAccrualDate(suggestion)

  return {
    ...suggestion,
    relations: mapRelationsForForm(suggestion.suggested_relations),
    attachment_ids: suggestion?.attachment_ids || [],
    attachment_exempt: attachmentExempt,
    blueprint: {
      ...blueprint,
      debet_entries: blueprint.debet_entries || [],
      credit_entries: blueprint.credit_entries || [],
      expense_entries: blueprint.expense_entries || []
    },
    posting_mode: defaultPostingMode,
    accrual_date: defaultAccrualDate
  }
}

export interface SuggestionFormProps {
  suggestion: IAccountingSuggestion
  attachmentExempt: boolean
  isVisible?: boolean
  onCreate: (data: IAccountingSuggestion) => Promise<any>
  onClose: () => void
  onValuesUpdate?: (values: Partial<IAccountingSuggestion>) => void
}

export const SuggestionForm: React.FC<SuggestionFormProps> = ({
  suggestion,
  attachmentExempt,
  isVisible = true,
  onCreate,
  onClose,
  onValuesUpdate
}) => {
  const [showContactInput, setShowContactInput] = useState(
    suggestion.contact == null && !suggestion.contact_name
  )
  const [showAcceptAmountConfirm, setShowAcceptAmountConfirm] = useState(false)
  const [confirmReason, setConfirmReason] = useState<ConfirmPostingWarningReason | null>(null)
  const pendingSubmitDataRef = useRef<SuggestionFormValues | null>(null)
  const wasVisibleRef = useRef(false)
  const calculatedAmountRef = useRef<number>(0)
  const debetCreditDiffRef = useRef<number | null>(null)
  const { slug, data: business } = useBusinessContext()

  const closeConfirmPrompt = useCallback(() => {
    setShowAcceptAmountConfirm(false)
    setConfirmReason(null)
    pendingSubmitDataRef.current = null
  }, [])
  const { t } = useTranslation()
  const isTrialing = business?.stripe_plan_status === 'trialing'
  const { data: trialLimits } = useTrialLimits({ enabled: isTrialing })
  const trialLimitReached =
    isTrialing &&
    trialLimits?.document_usage?.limit != null &&
    (trialLimits?.document_usage?.remaining ?? 0) <= 0
  const planName = getOptionLabelByValue(NOCFO_PLAN_OPTIONS, business?.subscription_plan, {
    t,
    fallback: '',
    fallbackToValue: true
  })
  const planSettingsUrl = getSettingsPageUrl(slug, { page: 'plan' })
  const suggestionFormDefaultValues = useMemo(
    () => getSuggestionFormDefaultValues(suggestion, attachmentExempt),
    [suggestion, attachmentExempt]
  )

  const methods = useForm<SuggestionFormValues>({
    defaultValues: suggestionFormDefaultValues
  })
  const {
    register,
    control,
    getValues,
    setValue,
    handleSubmit,
    reset,
    setError,
    formState: { isSubmitting, errors }
  } = methods

  const updateEntryAmounts = useUpdateEntryAmounts(getValues, setValue)
  const blueprint = useWatch({
    control,
    name: 'blueprint'
  })
  const contact = useWatch({
    control,
    name: 'contact'
  })
  const euSaleWarning = useEuSaleWarning(contact, blueprint)

  useEffect(() => {
    const justOpened = isVisible && !wasVisibleRef.current
    wasVisibleRef.current = isVisible

    if (!isVisible || !justOpened) return

    reset(suggestionFormDefaultValues)
    setShowContactInput(suggestion.contact == null && !suggestion.contact_name)
  }, [isVisible, reset, suggestion.contact, suggestion.contact_name, suggestionFormDefaultValues])

  const submitData = useCallback(
    async (newData: SuggestionFormValues) => {
      try {
        await onCreate(newData)
      } catch (error) {
        const { status, data } = error || {}
        if (status === 400) {
          if (data && typeof data === 'object' && !Array.isArray(data)) {
            setAPIErrors(data, setError)
          }
          const resolvedError = resolveAcceptSuggestionError(data)
          if (resolvedError) {
            toast.error(t(resolvedError))
          } else {
            toast.error(t('document.form.updateError'))
          }
        } else {
          toast.error(t('document.form.updateError'))
        }
        reset(newData, { keepErrors: true })
      }
    },
    [onCreate, setError, t, reset]
  )

  const onSubmit = handleSubmit(async data => {
    const newData = {
      ...data,
      blueprint: cleanBlueprint(data?.blueprint_type, data?.blueprint)
    } as SuggestionFormValues

    const warning = getConfirmPostingWarning({
      expectedAmount: expectedAmountForPaymentAccount(suggestion.import_data),
      calculatedAmount: calculatedAmountRef.current,
      debetCreditDiff: debetCreditDiffRef.current,
      euSaleWarning
    })
    if (warning) {
      pendingSubmitDataRef.current = newData
      setConfirmReason(warning.reason)
      setShowAcceptAmountConfirm(true)
      return
    }

    await submitData(newData)
  })

  const isAllowedToUpload = useAllowUpload()

  // WATCH VALUES
  // ============

  const values = useWatch({ control }) as Partial<IAccountingSuggestion>
  const watchedDate = useWatch({
    control,
    name: 'date'
  })
  const blueprintType = useWatch({
    control,
    name: 'blueprint_type'
  })
  const postingMode = useWatch({
    control,
    name: 'posting_mode'
  })
  const canUseAccrualSplit = suggestion.accrual_split.enabled
  const isSplitMode = canUseAccrualSplit && postingMode === 'accrual_split'
  const { documentLimits, isDocumentLimitReached } = useDocumentLimitStatus(watchedDate)
  useEffect(() => {
    if (onValuesUpdate) onValuesUpdate(values)
  }, [JSON.stringify(values)])

  // COPY FUNCTIONALITY
  // ==================

  const onCopy = useCallback(documentToCopy => {
    reset(
      {
        ...getValues(),
        ...getDocumentCopyValues(documentToCopy, suggestion.import_data)
      },
      { keepDefaultValues: true }
    )
  }, [])

  const { md } = useScreen()
  const isBillingLocked = isBusinessBillingLocked(business)

  const handleShowContactInput = (value: boolean) => {
    setShowContactInput(value)
  }

  return (
    <FormProvider {...methods}>
      <BlueprintPaymentAmountSync
        paymentAccountId={suggestion.import_data?.payment_account_id}
        calculatedAmountRef={calculatedAmountRef}
      />
      <BlueprintDebetCreditDiffSync debetCreditDiffRef={debetCreditDiffRef} />
      <StyledForm onSubmit={onSubmit}>
        <StyledFormContent>
          {!md && !isBillingLocked && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
              <DocumentHelpLink />
            </div>
          )}

          <Input
            label={t('document.date')}
            info={t('document.dateInfo')}
            type="date"
            required={true}
            {...register('date', {
              required: t('validation.required') as string
            })}
            errors={get(errors, 'date')}
          />

          {canUseAccrualSplit && (
            <>
              <Controller
                name="posting_mode"
                control={control}
                render={({ field }) => (
                  <Select
                    label={t('document.form.postingModeLabel')}
                    value={field.value || 'cash'}
                    onChange={field.onChange}
                  >
                    <option value="cash">{t('document.form.postingModeCash')}</option>
                    <option value="accrual_split">
                      {t('document.form.postingModeAccrualSplit')}
                    </option>
                  </Select>
                )}
              />

              {isSplitMode && (
                <Input
                  label={t('document.form.accrualDateLabel')}
                  type="date"
                  required={true}
                  {...register('accrual_date', {
                    required: t('validation.required') as string
                  })}
                  errors={get(errors, 'accrual_date')}
                />
              )}
            </>
          )}

          {trialLimitReached ? (
            <LimitNoticeWrapper>
              <InlineTeaserView
                icon={<FaExclamationTriangle />}
                iconText={t('document.form.trialLimitReachedTitle')}
                text={t('document.form.trialLimitReachedDescription', { planName })}
                ctaLabel={t('document.form.trialLimitReachedCta')}
                ctaTo={planSettingsUrl}
              />
            </LimitNoticeWrapper>
          ) : isDocumentLimitReached ? (
            <LimitNoticeWrapper>
              <InlineTeaserView
                icon={<FaExclamationTriangle />}
                iconText={t('document.form.limitReachedTitle')}
                text={t('document.form.limitReachedDescription', {
                  documentLimit: documentLimits?.limit ?? 0
                })}
              />
            </LimitNoticeWrapper>
          ) : null}

          {/* DESCRIPTION & COPY FUNCTIONALITY */}
          <Controller
            control={control}
            name="description"
            rules={{
              required: { value: true, message: t('validation.required') },
              validate: (val: string | undefined) =>
                val && val.trim().length > 0 ? true : t('validation.required')
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DocumentCopyDropdown
                key="document-copy-dropdown"
                label={t('document.description')}
                placeholder={t('document.descriptionPlaceholder')}
                info={t('document.descriptionInfo')}
                id="document-description"
                slug={slug}
                importModelId={suggestion.import_data.id}
                onChange={onChange}
                onCopy={onCopy}
                value={value}
                errors={error}
                required={true}
              />
            )}
          />

          <EuB2bVatFieldWrap $warn={euSaleWarning != null}>
            {showContactInput ? (
              <Controller
                name="contact"
                control={control}
                rules={{ required: { value: true, message: t('validation.required') } }}
                render={({ field, fieldState: { error } }) => (
                  <ContactSelector
                    {...field}
                    errors={error}
                    ref={null}
                    required={true}
                    fetchOnlyInvoicableContacts={false}
                    placeholder={t('document.contactPlaceholder')}
                    label={t('document.contact')}
                    info={t('document.contactInfo')}
                    noDataMessage={t('contacts.contactSelector.noDataDocumentForm')}
                  ></ContactSelector>
                )}
              />
            ) : (
              <ContactSuggestion
                handleShowContactInput={handleShowContactInput}
                suggestion={suggestion}
              />
            )}
            {euSaleWarning ? <EuB2bVatWarning contactId={contact} variant={euSaleWarning} /> : null}
          </EuB2bVatFieldWrap>

          {/* TAGS */}

          {business?.has_tags ? (
            <Controller
              name="tag_ids"
              control={control}
              render={({ field }) => (
                <StyledTagWrapper>
                  <TagSelector {...field} />
                </StyledTagWrapper>
              )}
            />
          ) : (
            <br />
          )}

          <Controller
            name="blueprint_type"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Select
                label={t('document.selectedType')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updateEntryAmounts(blueprintType)
                  onChange(event)
                }}
                value={value}
                errors={error}
                required={true}
              >
                {BLUEPRINT_TYPE_OPTIONS.map(({ value, labelKey }) => (
                  <option key={value} value={value}>
                    {t(labelKey)}
                  </option>
                ))}
              </Select>
            )}
          />
          <BlueprintEditor
            expectedPaymentAccount={suggestion.import_data.payment_account_id}
            expectedPaymentAccountAmount={expectedAmountForPaymentAccount(suggestion.import_data)}
          />

          <SuggestionRelationsSection>
            <Controller
              name="relations"
              control={control}
              render={({ field: { value, onChange } }) => (
                <RelationMapper
                  value={value}
                  onChange={onChange}
                  layoutVariant="form"
                  previewData={{
                    date: watchedDate,
                    contactId: typeof contact === 'number' ? contact : null,
                    blueprint
                  }}
                  renderHeader={({ title, lead, addMenu, themeMode }) => (
                    <RelationsHeader $themeMode={themeMode}>
                      <FormSectionEyebrow $hasLead={Boolean(lead)}>
                        {lead ?? title}
                      </FormSectionEyebrow>
                      {addMenu}
                    </RelationsHeader>
                  )}
                />
              )}
            />
          </SuggestionRelationsSection>

          <SuggestionAttachmentsSection>
            <FormSectionEyebrow>{t('document.attachments')}</FormSectionEyebrow>
            <ControlledDocumentAttachmentsField control={control} disabled={!isAllowedToUpload} />
          </SuggestionAttachmentsSection>
        </StyledFormContent>

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button type="button" onClick={() => onClose()}>
            {t('general.cancel')}
          </Button>
          <Button
            intent="success"
            icon={<FaCheck />}
            disabled={isSubmitting}
            showSpinner={isSubmitting}
          >
            {t('general.accept')}
          </Button>
        </div>
      </StyledForm>

      <Prompt
        isVisible={showAcceptAmountConfirm}
        onClose={closeConfirmPrompt}
        title={confirmReason ? t(postingWarningTitleKey(confirmReason)) : undefined}
        description={confirmReason ? t(postingWarningDescriptionKey(confirmReason)) : undefined}
        buttons={[
          {
            text: t('general.cancel'),
            intent: 'default',
            action: closeConfirmPrompt
          },
          {
            text: t('general.accept'),
            intent: 'success',
            action: async () => {
              const payload = pendingSubmitDataRef.current
              closeConfirmPrompt()
              if (payload) await submitData(payload)
            }
          }
        ]}
      />
    </FormProvider>
  )
}

const StyledForm = styled.form`
  height: 100%;
  display: flex;
  flex-direction: column;
`

const StyledFormContent = styled.div`
  flex: 1;
  overflow: auto;
`

const StyledTagWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
  margin-left: ${({ theme }) => theme.spacing.xxxs}rem;
`

const LimitNoticeWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const SuggestionRelationsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
`

const SuggestionAttachmentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
